/**
 * SOC2TrustBadge — live trust badge that pings /api/pilot-activation/readiness-score
 * and displays the current pass-count (e.g. "12/12 controls passing").
 *
 * Replaces a static "SOC2 Ready" pill in the Landing footer / hero with a
 * live-updating signal of CARTIE's own audit posture — the founder's
 * vendor-trust pledge made machine-checkable.
 */
import React, { useEffect, useState } from 'react';
import { Shield, ShieldCheck, ShieldAlert } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SOC2TrustBadge({ variant = 'footer' }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/pilot-activation/readiness-score`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err || !data) {
    // Graceful fallback — matches the previous static pill
    return (
      <div className="flex items-center gap-2 text-slate-400" data-testid="soc2-badge-fallback">
        <Shield className="w-4 h-4 text-blue-400" />
        <span className="text-xs">SOC2 Ready</span>
      </div>
    );
  }

  const pct = Math.round(data.score_pct ?? 0);
  const ok = data.ok_count ?? 0;
  const total = data.total_count ?? 0;
  const Icon = pct >= 100 ? ShieldCheck : pct >= 80 ? Shield : ShieldAlert;
  const color = pct >= 100 ? 'text-emerald-400' : pct >= 80 ? 'text-blue-400' : 'text-amber-400';

  if (variant === 'inline') {
    return (
      <a
        href="/soc2-readiness"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
        data-testid="soc2-trust-badge-inline"
        title={`Last evaluated ${new Date(data.evaluated_at).toLocaleString()}`}
      >
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs font-semibold text-emerald-200 tabular-nums">{ok}/{total} controls passing</span>
        <span className="text-[10px] text-emerald-300/80 font-mono">{pct}%</span>
      </a>
    );
  }

  return (
    <a
      href="/soc2-readiness"
      className="flex items-center gap-2 text-slate-400 hover:text-emerald-300 transition-colors group"
      data-testid="soc2-trust-badge"
      title={`Last evaluated ${new Date(data.evaluated_at).toLocaleString()}`}
    >
      <Icon className={`w-4 h-4 ${color} group-hover:scale-110 transition-transform`} />
      <span className="text-xs tabular-nums">
        <span className="font-semibold">{ok}/{total}</span> controls passing
      </span>
      <span className="text-[10px] text-slate-500 font-mono">{pct}%</span>
    </a>
  );
}
